import React, { useState, useEffect, useMemo, useCallback } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@shared/ui/hover-card";
import {
  Users,
  MapPin,
  Activity,
  Clock,
  CheckCircle,
  Search,
  Plus,
  Star,
  Zap,
  Info,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  useAssignedJobCards,
  useFieldForceRecommend,
  useJobCardRecommendation,
} from "../../../hooks";
import { transformJobCardRecommendation } from "../../../../utils";
import {
  SOPTemplate,
  RecommendedTechnician,
} from "@features/wx/dispatcher/types";
import { useSearchParams } from "react-router-dom";

// Helper function to build meter locations from selected entities
const buildMeterLocations = (
  selectedEntities: any[],
  serviceAssociation: string
): string[] => {
  // Only include meter locations if association is "Meter"
  if (serviceAssociation !== "Meter") {
    return [];
  }

  const validEntities = selectedEntities.filter((entity, index) => {
    // Try multiple possible locations for coordinates
    const possibleCoords = {
      "originalData.lat": entity.originalData?.lat,
      "originalData.long": entity.originalData?.long,
      "originalData.latitude": entity.originalData?.latitude,
      "originalData.longitude": entity.originalData?.longitude,
      lat: entity.lat,
      long: entity.long,
      latitude: entity.latitude,
      longitude: entity.longitude,
    };

    // Try different field combinations
    const lat =
      entity.originalData?.lat ||
      entity.originalData?.latitude ||
      entity.lat ||
      entity.latitude;

    const long =
      entity.originalData?.long ||
      entity.originalData?.lng ||
      entity.originalData?.longitude ||
      entity.long ||
      entity.lng ||
      entity.longitude;

    const isValid =
      lat &&
      long &&
      lat !== "NA" &&
      long !== "NA" &&
      lat !== null &&
      long !== null &&
      lat !== "" &&
      long !== "" &&
      !isNaN(Number(lat)) &&
      !isNaN(Number(long));

    return isValid;
  });

  const locations = validEntities.map((entity, index) => {
    const lat =
      entity.originalData?.lat ||
      entity.originalData?.latitude ||
      entity.lat ||
      entity.latitude;

    const long =
      entity.originalData?.long ||
      entity.originalData?.lng ||
      entity.originalData?.longitude ||
      entity.long ||
      entity.lng ||
      entity.longitude;

    const locationString = `(${lat}, ${long})`;

    return locationString;
  });

  return locations;
};

// Define search filters type
interface TechnicianFilters {
  search_data?: string;
  page: number;
  limit: number;
}

export interface AvailableTechniciansCardProps {
  selectedSOP: SOPTemplate | null;
  selectedEntities: any[];
  onTechnicianSelect?: (technician: RecommendedTechnician) => void;
}

export const AvailableTechniciansCard: React.FC<
  AvailableTechniciansCardProps
> = ({ selectedSOP, selectedEntities, onTechnicianSelect }) => {
  // URL-based search state management
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = 5;

  // Parse filters from URL params - following EntitySelectionSection pattern
  const getFiltersFromUrl = (): TechnicianFilters => {
    const page = parseInt(searchParams.get("techPage") || "1");

    const urlFilters: TechnicianFilters = {
      page,
      limit: itemsPerPage,
    };

    // Map 'techQuery' URL param to 'search_data' API param
    const queryParam = searchParams.get("techQuery");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<TechnicianFilters>(getFiltersFromUrl);
  const [searchInputValue, setSearchInputValue] = useState(
    filters.search_data || ""
  );

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams]);

  const [selectedTechnician, setSelectedTechnician] = useState<string | null>(
    null
  );
  const [showRecommendedSOPs, setShowRecommendedSOPs] = useState(false);
  const [currentTechnician, setCurrentTechnician] = useState<any>(null);
  const [selectedRecommendedSOPs, setSelectedRecommendedSOPs] = useState<
    string[]
  >([]);

  const { mutate: assignJobCards } = useAssignedJobCards();

  // Build meter locations based on selected entities and association type
  const meterLocations = useMemo(() => {
    if (!selectedSOP || !selectedEntities.length) {
      return [];
    }

    const locations = buildMeterLocations(
      selectedEntities,
      selectedSOP.association
    );

    return locations;
  }, [selectedSOP, selectedEntities]);

  // API call for field force recommendations with server-side search and pagination
  const {
    data: recommendationData,
    isLoading: isLoadingRecommendations,
    error: recommendationError,
    refetch: refetchRecommendations,
  } = useFieldForceRecommend(
    {
      sopId: selectedSOP?.id?.toString() || "",
      meterLocations: meterLocations,
      searchData: filters.search_data || "", // Add search parameter to API call
      page: filters.page, // Add page parameter
      limit: filters.limit, // Add limit parameter
    },
    {
      enabled: !!selectedSOP?.id, // Only call when SOP is selected
    }
  );

  // API call for job card recommendations (for the modal)
  const {
    data: jobCardData,
    isLoading: isLoadingJobCards,
    error: jobCardError,
    refetch: refetchJobCards,
  } = useJobCardRecommendation(
    {
      fieldForceId: currentTechnician?.fieldForceId,
    },
    {
      enabled: !!currentTechnician?.fieldForceId,
    }
  );

  // Transform API data to match existing UI expectations
  const transformTechnicianData = (technicians: RecommendedTechnician[]) => {
    return technicians.map((tech) => ({
      fieldForceId: tech.fieldForceId,
      name: tech.name,
      workload: tech.currentWorkload || 0,
      distance: tech.distance,
      distanceFrom: `Near selected ${
        selectedSOP?.association?.toLowerCase() || "location"
      }`,
      skills: tech.skill || [],
      skillMatch: tech.skillMatch,
      status:
        tech.availabilityScore >= 100
          ? "available"
          : tech.currentWorkload >= 80
          ? "overloaded"
          : "busy",
      utilization: tech.currentWorkload || 0,
      rating: tech.rating || 0,
      bestMatch: tech.matchPercentage || 0,
      tagline: tech.recommendedReason || `${tech.roleName}`,
      fieldForceNo: tech.fieldForceNo,
      contactNumber: tech.contactNumber,
      email: tech.email,
      originalData: tech,
    }));
  };

  // Use real API data or empty array - NO CLIENT-SIDE FILTERING
  const technicians = recommendationData?.results
    ? transformTechnicianData(recommendationData.results)
    : [];

  // Get total count from API response
  const totalCount = recommendationData?.count || 0;

  // Calculate pagination info
  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const currentPage = filters.page;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return {
      currentPage,
      totalPages,
      totalItems: totalCount,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [totalCount, filters.page, itemsPerPage]);

  const debouncedSearch = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const params = new URLSearchParams(searchParams);
        if (searchTerm.trim()) {
          params.set("techQuery", searchTerm);
        } else {
          params.delete("techQuery");
        }
        // Reset to page 1 when searching
        params.set("techPage", "1");
        setSearchParams(params);
      }, 500);
    };
  }, [setSearchParams, searchParams]);

  useEffect(() => {
    setSearchInputValue(filters.search_data || "");
  }, [filters.search_data]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("techPage", newPage.toString());
    setSearchParams(params);
  };

  const getWorkloadColor = (workload: number) => {
    if (workload === 0) return "text-green-600";
    if (workload >= 6) return "text-red-600";
    return "text-blue-600";
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization <= 30) return "text-green-600";
    if (utilization >= 80) return "text-red-600";
    return "text-blue-600";
  };

  const getBestMatchColor = (bestMatch: number) => {
    if (bestMatch >= 90) return "text-green-600";
    if (bestMatch >= 70) return "text-blue-600";
    return "text-yellow-600";
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-700";
      case "overloaded":
        return "bg-red-100 text-red-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "bg-red-100 text-red-700";
      case "Medium":
        return "bg-yellow-100 text-yellow-700";
      case "Low":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getMatchColor = (matchPercentage: number) => {
    if (matchPercentage >= 90) return "text-green-600";
    if (matchPercentage >= 70) return "text-blue-600";
    return "text-yellow-600";
  };

  const handleTechnicianClick = (technician: any) => {
    setSelectedTechnician(technician.fieldForceId);
    onTechnicianSelect?.(technician);
  };

  const handleNearbySopClick = (technician: any) => {
    setSelectedTechnician(technician.fieldForceId);
    setCurrentTechnician(technician);
    setShowRecommendedSOPs(true);
    onTechnicianSelect?.(technician);
  };

  const handleRecommendedSOPToggle = (sopId: string) => {
    setSelectedRecommendedSOPs((prev) =>
      prev.includes(sopId)
        ? prev.filter((id) => id !== sopId)
        : [...prev, sopId]
    );
  };

  const handleAssignSOPs = () => {
    assignJobCards({
      job_card_ids: selectedRecommendedSOPs,
      field_force_id: selectedTechnician,
      
    });
    setShowRecommendedSOPs(false);
    setSelectedRecommendedSOPs([]);
  };

  const handleAutoAssign = () => {
    const availableTechnicians = technicians.filter(
      (tech) => tech.status === "available"
    );

    if (availableTechnicians.length === 0) return;

    // Sort by bestMatch descending and pick the first one (handles ties by picking first)
    const sortedTechnicians = availableTechnicians.sort(
      (a, b) => b.bestMatch - a.bestMatch
    );
    const bestTechnician = sortedTechnicians[0];

    console.log(
      `Auto-assigning best match technician: ${bestTechnician.name} with ${bestTechnician.bestMatch}% match`
    );

    handleTechnicianClick(bestTechnician);
  };

  // Transform API data for display (job cards in modal)
  const recommendedSOPs =
    jobCardData?.result?.map(transformJobCardRecommendation) || [];

  // Show message when no SOP is selected
  if (!selectedSOP) {
    return (
      <Card className="bg-white shadow-sm">
        <CardContent className="p-6">
          <div className="text-center py-8 text-gray-500">
            <Users className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm">
              Select an SOP to see available technicians
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Available Technicians
            </CardTitle>
            {technicians.length > 0 && (
              <Button
                onClick={handleAutoAssign}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
                disabled={isLoadingRecommendations}
              >
                <Zap className="h-4 w-4" />
                Auto Assign
              </Button>
            )}
          </div>
          {/* Server-side search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, ID, or skills..."
              value={searchInputValue}
              onChange={(e) => {
                setSearchInputValue(e.target.value);
                debouncedSearch(e.target.value);
              }}
              className="pl-10 text-sm"
              disabled={isLoadingRecommendations}
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Loading State */}
          {isLoadingRecommendations ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-600">Loading technicians...</span>
            </div>
          ) : recommendationError ? (
            <div className="text-center py-8 text-red-600">
              <p className="text-sm">
                Failed to load technician recommendations
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchRecommendations()}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          ) : technicians.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p className="text-sm">
                {filters.search_data
                  ? `No technicians found matching "${filters.search_data}"`
                  : "No technicians available for this SOP"}
              </p>
              {selectedSOP.association === "Meter" &&
                selectedEntities.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Select meters to get location-based recommendations
                  </p>
                )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {/* Show search results count and pagination info */}
                <div className="flex items-center justify-between text-sm text-gray-600 border-b pb-2">
                  <span>
                    {filters.search_data && (
                      <span>
                        Found {totalCount} technician(s) matching "
                        {filters.search_data}"
                      </span>
                    )}
                    {!filters.search_data && (
                      <span>
                        Showing {pagination.startItem} - {pagination.endItem} of{" "}
                        {totalCount} technicians
                      </span>
                    )}
                  </span>
                  <span>
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                </div>

                {/* Technician List */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {technicians.map((technician) => (
                    <div
                      key={technician.fieldForceId}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors relative ${
                        selectedTechnician === technician.fieldForceId
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => handleTechnicianClick(technician)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-xs">
                            {technician.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">
                              {technician.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {technician.fieldForceId}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-500" />
                            <span
                              className={`text-xs font-medium ${getBestMatchColor(
                                technician.bestMatch
                              )}`}
                            >
                              {technician.bestMatch}% Match
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-500" />
                            <span className="text-xs text-gray-500 font-medium">
                              {technician.distance}
                            </span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  Distance from: {technician.distanceFrom}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-500" />
                          <span className="text-xs text-gray-500">
                            Workload:{" "}
                          </span>
                          <span
                            className={`font-medium text-xs ${getWorkloadColor(
                              technician.workload
                            )}`}
                          >
                            {technician.workload}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Activity className="h-3 w-3 text-gray-500" />
                          <span className="text-xs text-gray-500">
                            Utilization:{" "}
                          </span>
                          <span
                            className={`font-medium text-xs ${getUtilizationColor(
                              technician.utilization
                            )}`}
                          >
                            {technician.utilization}%
                          </span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex items-center gap-1 mb-1">
                          <CheckCircle className="h-3 w-3 text-gray-500" />
                          <p className="text-xs text-gray-500">Skills:</p>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-wrap gap-1 cursor-help">
                              {technician.skills.slice(0, 2).map((skill) => (
                                <Badge
                                  key={skill}
                                  variant="outline"
                                  className="text-xs px-1 py-0"
                                >
                                  {skill}
                                </Badge>
                              ))}
                              {technician.skills.length > 2 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs px-1 py-0"
                                >
                                  +{technician.skills.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64">
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold">
                                All Skills
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {technician.skills.map((skill) => (
                                  <Badge
                                    key={skill}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      {/* Nearby SOP Button - Bottom Right */}
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNearbySopClick(technician);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-7"
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          Nearby SOP
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {
                  <div className="flex items-center justify-center gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handlePageChange(
                          Math.max(1, pagination.currentPage - 1)
                        )
                      }
                      disabled={pagination.currentPage === 1}
                      className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    {/* Show page numbers dynamically */}
                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        // Calculate which pages to show
                        const startPage = Math.max(
                          1,
                          pagination.currentPage - 2
                        );
                        const endPage = Math.min(
                          pagination.totalPages,
                          startPage + 4
                        );
                        const adjustedStartPage = Math.max(1, endPage - 4);

                        const page = adjustedStartPage + i;
                        if (page > pagination.totalPages) return null;

                        return (
                          <Button
                            key={page}
                            variant={
                              pagination.currentPage === page
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 p-0 ${
                              pagination.currentPage === page
                                ? "bg-blue-600 text-white"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </Button>
                        );
                      }
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handlePageChange(
                          Math.min(
                            pagination.totalPages,
                            pagination.currentPage + 1
                          )
                        )
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                }
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recommended SOPs Modal - unchanged */}
      <Dialog open={showRecommendedSOPs} onOpenChange={setShowRecommendedSOPs}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Recommended SOPs for {currentTechnician?.name}
              {currentTechnician && (
                <Badge className="ml-2 bg-green-100 text-green-700">
                  Best Match: {currentTechnician.bestMatch}%
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Below are the SOPs located near the same area as the selected SOP.
            </p>

            {isLoadingJobCards ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">
                  Loading recommendations...
                </p>
              </div>
            ) : jobCardError ? (
              <div className="text-center py-8 text-red-600">
                <p className="text-sm">Failed to load recommendations</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchJobCards()}
                  className="mt-2"
                >
                  Retry
                </Button>
              </div>
            ) : recommendedSOPs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm">
                  No recommended SOPs found for this technician
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {recommendedSOPs.map((sop) => {
                  return (
                    <div
                      key={sop.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedRecommendedSOPs.includes(sop.id)
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleRecommendedSOPToggle(sop.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-gray-900">
                              {sop.id}
                            </span>

                            <Badge className="bg-purple-100 text-purple-700">
                              <span
                                className={getMatchColor(sop.matchPercentage)}
                              >
                                {sop.matchPercentage}% Match
                              </span>
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">
                            {sop.type}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {sop.areaName} ({sop.subAreaName})
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {sop.estimatedTime}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <span className="text-gray-500">Scores:</span>
                            <span className="text-blue-600">
                              Location: {sop.locationScore}
                            </span>
                            <span className="text-green-600">
                              Skill: {sop.skillScore}
                            </span>
                            <span className="text-orange-600">
                              Availability: {sop.availabilityScore}
                            </span>
                          </div>
                        </div>
                        {selectedRecommendedSOPs.includes(sop.id) && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRecommendedSOPs(false)}
            >
              Skip
            </Button>
            <Button
              onClick={handleAssignSOPs}
              disabled={selectedRecommendedSOPs.length === 0}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Assign Selected SOPs ({selectedRecommendedSOPs.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
